/**
 * @render react
 * @name PrimaryMedia
 * @example 1
 * <PrimaryMedia mediaType="Image" src="image.jpg" alt="Alt text" caption="Caption text" credits="Credits text"/>
 * @example 2
 * <PrimaryMedia mediaType="Video" src="http://path-to-video.mp4" />
 * @example 3
 * <PrimaryMedia mediaType="LinkedMedia" linkType="GALLERY_LINKOUT" src="http://path-to-link" item={...}/>
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';
import { Podcast } from 'Components/molecules/Podcast';
import { Social } from 'Components/molecules/Social';
import { SocialEmbed } from 'Components/molecules/SocialEmbed';
import { Video } from 'Components/molecules/Video';
import { Gallery } from 'Components/organisms/Gallery';

import './_style.scss';

/**
 * PrimaryMedia component
 *
 * @param {string} type - type of media to be outputted
 * @param {string} src - source of the media
 * @param {string} alt - alt attribute for Image
 * @param {string} caption - caption attribute for Image
 * @param {string} credits - credits attribute for Image
 * @param {object} context - extra information coming from Polaris provider
 * @param {string} cssPrefix
 * @param {string|number} height
 * @param {string} mediaType
 * @param {object} page - props to pass to Gallery
 * @param {string} linkType - link type for the Gallery - can be GALLERY_LINKOUT or GALLERY_SLIDESHOW
 * @param {boolean} mediaLazyLoad - Defines whether the image media needs to be lazyLoaded or not
 * @param {string} mediaSize - mediaSize for PrimaryMedia component
 * @param {Object} badge - Object for the badge overlay, contains src (string) and alt (string)
 * @param {Object} extraComponentProps - Object with extra props to pass down to the Gallery component
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra classes to be added to the component
 * @param {Object} context - Extra information coming from Polaris provider
 * @param podCastData
 * @param {string} provider
 * @param socialEmbed
 * @param {array} socialPlatforms
 * @param {string} title
 * @param {string} url
 * @param {string|number} width
 * @param {boolean} isLCPCandidate - Defines whether the component is an LCP candidate or not. Default to true.
 */

const PrimaryMedia = ({
  alt,
  author,
  authorUrl,
  badge,
  caption,
  context: { cssPrefix },
  credits,
  extraClassNames,
  extraComponentProps,
  height,
  href,
  images,
  isLCPCandidate,
  linkType,
  loadStyles,
  mediaLazyLoad,
  mediaSize,
  mediaType,
  page,
  podCastData,
  provider,
  renderPictureTag,
  shareUrl,
  socialEmbed,
  socialPlatforms,
  src,
  title,
  type,
  url,
  width,
}) => {
  // Check if we have a gallery
  let galleryMode = 'linkout';
  let galleryCover = null;
  let galleryUrl = null;
  let galleryImages = [];
  let isSingleMedia = false;

  if (mediaType === 'LinkedMedia') {
    if (linkType === 'GALLERY_LINKED') {
      // we need cover for gallery linked
      if (page.primaryMedia) {
        // Set the cover.
        galleryCover = {
          ...page.primaryMedia,
          isLCPCandidate,
          size: mediaSize,
        };
        galleryUrl = page.url;
      }
    } else if (linkType === 'GALLERY_SLIDESHOW') {
      galleryMode = 'swipe';
    }

    if (page.images) {
      galleryImages = page.images;
    }
  }

  let imageProps = {
    src,
    alt: alt || '',
    caption,
    credits,
    displayImageMetadata: true,
    disableTransforms: mediaSize === null,
    height,
    images,
    isLCPCandidate,
    lazyLoad: mediaLazyLoad,
    size: mediaSize,
    renderPictureTag,
    width,
  };

  // @TODO Check if needed
  let validTranfSize = mediaSize;
  if (type && type === 'SingleMedia') {
    isSingleMedia = true;
    const isValidTranfName = typeof mediaSize === 'string';
    validTranfSize = isValidTranfName ? mediaSize : 'single-media-image';

    imageProps = {
      ...imageProps,
      displayImageMetadata: false, // display image metadata outside the Image Component
      size: validTranfSize,
    };
  }

  return (
    <div
      className={classNames({
        [`${cssPrefix}__primary-media`]: loadStyles,
        '-has-badge': badge && badge.src,
        '-has-image-metadata': mediaType === 'Image' && (caption || credits),
        '-single-media': isSingleMedia,
        ...extraClassNames,
      })}
    >
      {badge && badge.src !== '' && (
        <Image
          {...badge}
          ampLayout="fixed"
          extraClassNames={{ [`${cssPrefix}__primary-media--badge`]: true }}
        />
      )}

      {mediaType === 'Video' && (
        <Video url={src} />
      )}

      {mediaType === 'Image' && (
        href ? (
          <Link
            href={href}
            screenReaderText={title}
          >
            <Image {...imageProps} />
          </Link>
        ) : <Image {...imageProps} />
      )}

      {mediaType === 'LinkedMedia' && (
        <Gallery
          {...extraComponentProps}
          cover={galleryCover}
          media={galleryImages}
          mediaLazyLoad={mediaLazyLoad}
          mode={galleryMode}
          url={galleryUrl}
        />
      )}

      {mediaType === 'SocialEmbed' && (
        <SocialEmbed
          type={provider}
          url={url}
        >
          {socialEmbed}
        </SocialEmbed>
      )}

      {mediaType === 'Podcast' && podCastData && (
        <Podcast
          {...podCastData}
        />
      )}

      {isSingleMedia && (
        <div
          className={`${cssPrefix}__single-media--meta`}
        >
          {mediaType && mediaType === 'SocialEmbed' ? (
            <div
              className={`${cssPrefix}__social-embed--meta`}
            >
              {title && (
                <div className={`${cssPrefix}__social-embed--title`}>
                  {title}
                </div>
              )}
              {author && (
                <div className={`${cssPrefix}__social-embed--author`}>
                  {authorUrl ? (
                    <Link
                      ariaLabel={author}
                      extraClassNames={{
                        [`${cssPrefix}__social-embed--author-url`]: true,
                      }}
                      href={authorUrl}
                      title={author}
                    >
                      {author}
                    </Link>
                  ) : (
                    { author }
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              className={`${cssPrefix}__image--meta`}
            >
              {caption && (
                <div className={`${cssPrefix}__image--caption`}>{caption}</div>
              )}
              {credits && (
                <div
                  className={`${cssPrefix}__image--credits`}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: credits }}
                />
              )}
            </div>
          )}

          {socialPlatforms && socialPlatforms.length > 1 && (
            <Social
              extraClassNames={{
                '-single-media': true,
              }}
              platforms={socialPlatforms}
              title={title}
              url={shareUrl || url}
            />
          )}
        </div>
      )}
    </div>
  );
};

PrimaryMedia.propTypes = {
  alt: PropTypes.string,
  author: PropTypes.string,
  authorUrl: PropTypes.string,
  badge: PropTypes.shape({
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    src: PropTypes.string,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  caption: PropTypes.string,
  context: PropTypes.shape(),
  credits: PropTypes.string,
  extraClassNames: PropTypes.shape(),
  extraComponentProps: PropTypes.shape(),
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  href: PropTypes.string,
  images: PropTypes.arrayOf(PropTypes.shape()),
  isLCPCandidate: PropTypes.bool,
  linkType: PropTypes.string,
  loadStyles: PropTypes.bool,
  mediaLazyLoad: PropTypes.bool,
  mediaSize: PropTypes.string,
  mediaType: PropTypes.string.isRequired,
  page: PropTypes.shape(),
  podCastData: PropTypes.shape(),
  provider: PropTypes.string,
  renderPictureTag: PropTypes.bool,
  shareUrl: PropTypes.string,
  socialEmbed: PropTypes.node,
  socialPlatforms: PropTypes.arrayOf(PropTypes.shape()),
  src: PropTypes.string,
  title: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PrimaryMedia.defaultProps = {
  alt: '',
  author: '',
  authorUrl: '',
  badge: {
    alt: '',
    height: 0,
    src: '',
    width: 0,
  },
  caption: '',
  context: {},
  credits: '',
  extraClassNames: {},
  extraComponentProps: {},
  images: [],
  linkType: '',
  loadStyles: true,
  height: 0,
  href: null,
  isLCPCandidate: true,
  mediaLazyLoad: false,
  mediaSize: 'primary-image',
  page: {},
  podCastData: null,
  provider: null,
  renderPictureTag: false,
  shareUrl: null,
  socialEmbed: null,
  socialPlatforms: null,
  src: '',
  title: null,
  type: 'article',
  url: null,
  width: 0,
};

export default withPolaris(PrimaryMedia);
